import { z } from 'zod';
import { itemURN, CategoryID } from '../ads/zod.js';
import { geoEntry } from '../geo/zod.js';

const userID = z.string().min(5).max(9);
const userStatus = z.enum(['ACTIVE', 'UNVERIFIED']);
const userGender = z.enum(['M', 'F']);
const user = z.object({
    userID,
    email: z.string().email(),
    status: userStatus,
    creationDate: z.string().datetime({ offset: true }),
});
const users = z.array(user);
const productSubscription = z.object({
    categories: z.array(z.object({
        key: z.string(),
        value: z.string(),
    })),
});
const userSubscription = z.object({
    subscriptionId: z.number(),
    statusSubscriptions: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    product: productSubscription,
});
const userSubscriptions = z.object({
    subscriptions: z.array(userSubscription),
});
const loggedUser = z.object({
    userID,
    email: z.string().email(),
    username: z.string(),
});
const userProfileInfo = z.object({
    username: z.string(),
    showPhone: z.boolean(),
    isDefaultName: z.boolean().optional(),
    picture: z.string().optional(),
});
const userProfilePhone = z.object({
    show: z.boolean(),
    number: z.string().optional(),
    prefix: z.string().optional(),
});
const userProfile = z.object({
    username: z.string(),
    birthdate: z.string().optional(),
    geo: geoEntry.optional(),
    sex: userGender.optional(),
    phone: userProfilePhone.optional(),
});
const userContactInfo = z.object({
    adURN: itemURN,
    phoneNumber: z.string(),
    force: z.boolean(),
});
const badgeStatus = z.object({
    assignedAt: z.string().datetime({ offset: true }).nullish(),
    label: z.string().nullish(),
    earned: z.boolean().nullish(),
    currentValue: z.number().int().nonnegative().nullish(),
    targetValue: z.number().int().nonnegative().nullish(),
});
const badge = z.object({
    id: z.string().uuid(),
    icon: z.string().nullish(),
    name: z.string().nullish(),
    description: z.string().nullish(),
    instructions: z.string().nullish(),
    info: z.string().nullish(),
    infoIcon: z.string().nullish(),
    isActive: z.boolean().nullish(),
    categories: z.array(z.nativeEnum(CategoryID)).nullish(),
    status: badgeStatus.nullish(),
});
const userAdInfo = z.object({
    firstAdInsertionDate: z.string(),
    totalApprovedAds: z.number(),
    userCategoriesInfo: z.array(z.object({
        category: z.nativeEnum(CategoryID),
        approvedAds: z.number(),
    })),
    badges: z.array(badge).nullish(),
});
const userEmail = z.object({
    email: z.string(),
    date: z.string(),
    channel: z.string(),
    adminId: z.string(),
});
const userEmails = z.object({ history: z.array(userEmail) });
const changeEmailSession = z.object({
    newEmail: z.string(),
    id: z.string(),
    challenges: z.array(z.object({
        type: z.enum(['account', 'new_email']),
        methods: z.array(z.enum(['email', 'phone'])),
    })),
});
const changeEmailProcess = z.object({
    account: z.object({
        email: z.string(),
        phone: z.optional(z.string()),
        hasSafeSession: z.optional(z.boolean()),
    }),
    session: z.optional(changeEmailSession),
});
const changeEmail2FAChallenge = z.object({
    token: z.string(),
});
const changeEmailIsEligible = z.object({
    isEligible: z.boolean(),
});
const userDac7Info = z.object({
    firstName: z.string(),
    lastName: z.string(),
    countryOfBirth: z.string(),
    cityOfBirth: z.string(),
    country: z.string(),
    address: z.string(),
    dateOfBirth: z.string(),
    city: z.string(),
    town: z.string(),
    postalCode: z.string(),
    taxCode: z.string(),
    iban: z.array(z.string()),
    paypal: z.array(z.string()),
    disclaimer: z.boolean(),
});
const user2FAChallenges = z.object({
    availableFactors: z.array(z.string()),
    defaultFactor: z.string(),
});
const user2FAChallenge = z.object({
    remainingAttempts: z.number(),
    recipient: z.string(),
});

export { badge, badgeStatus, changeEmail2FAChallenge, changeEmailIsEligible, changeEmailProcess, changeEmailSession, loggedUser, productSubscription, user, user2FAChallenge, user2FAChallenges, userAdInfo, userContactInfo, userDac7Info, userEmail, userEmails, userGender, userID, userProfile, userProfileInfo, userProfilePhone, userStatus, userSubscription, userSubscriptions, users };
//# sourceMappingURL=zod.js.map
